import React, { useState, useEffect, useRef } from 'react'
import { useHistory, useLocation } from 'react-router-dom'
import Grid from '@mui/material/Grid'
import Paper from '@mui/material/Paper'
import Box from '@mui/material/Box'
import FormControl from '@mui/material/FormControl'
import FormControlLabel from '@mui/material/FormControlLabel'
import Radio from '@mui/material/Radio'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'
import Title from '../../component/Title'
import API from '../../component/Axios'
import Snackbars from '../../component/Snackbars'
import BottomButtonGroup from '../../component/BottomButtonGroup'
import Spiner from '../../component/Spiner'
import ListEmptyState from '../../component/ListEmptyState'
import { FORM_PANEL_SX, getFormChoiceCardSx } from '../../component/formSurfaceStyles'
import { createListLinkTarget, getLocationState } from '../../utils/listPageState'
import { ADMIN_ACCESS_MODES } from '../../utils/sessionUtils'
import {
    applyAdminDraft,
    clearAdminDraft,
    createAdminDraftPayload,
    createEditAdminDraftKey,
    readAdminDraft,
    saveAdminDraft,
} from './adminDraftUtils'

const PHONE_REGEX = /(?:\d{3}|\d{4})\d{4}$/
const EMAIL_REGEX = /^[0-9a-zA-Z]([-_.]?[0-9a-zA-Z])*@[0-9a-zA-Z]([-_.]?[0-9a-zA-Z])*.[a-zA-Z]{2,3}$/i
const DEFAULT_SEARCH_SELECT = {
    searchRole: '',
    searchStatus: '',
}

const PANEL_SX = FORM_PANEL_SX

const getChoiceCardSx = (selected, disabled = false) => ({
    ...getFormChoiceCardSx(selected, disabled),
    p: 1.5,
    width: '100%',
})

const BASE_ROLE_OPTIONS = [
    {
        value: 0,
        title: '매니저',
        description: '기본 운영 업무를 처리하는 관리자 권한입니다.',
    },
]

const SUPER_ADMIN_ROLE_OPTIONS = [
    {
        value: 1,
        title: '어드민',
        description: '관리자 기능 전반을 사용할 수 있는 권한입니다.',
    },
    {
        value: 2,
        title: '슈퍼어드민',
        description: '상위 관리자 권한까지 관리할 수 있는 최고 권한입니다.',
    },
]

const getRoleOptions = (currentRole) => (
    currentRole === 5
        ? [...BASE_ROLE_OPTIONS, ...SUPER_ADMIN_ROLE_OPTIONS]
        : BASE_ROLE_OPTIONS
)

const ACCESS_MODE_OPTIONS = [
    {
        value: ADMIN_ACCESS_MODES.DEFAULT,
        title: '전체 접근',
        description: '등급에 따른 기존 관리자 메뉴를 사용합니다.',
    },
    {
        value: ADMIN_ACCESS_MODES.COUNTRY_ANALYTICS_ONLY,
        title: '국가별 지표 전용',
        description: '국가별 지표 페이지에서 DAU/MAU와 회원가입수만 볼 수 있습니다.',
    },
]

function FormSection({ title, description, children }) {
    return (
        <Paper variant="outlined" sx={PANEL_SX}>
            <Box sx={{ mb: 1.5 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                    {title}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                    {description}
                </Typography>
            </Box>
            {children}
        </Paper>
    )
}

// 권한 라디오 옵션에 설명을 함께 보여 운영자가 선택 범위를 바로 구분하게 한다.
function ChoiceCardLabel({ title, description }) {
    return (
        <Box>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>
                {title}
            </Typography>
            <Typography variant="caption" color="text.secondary">
                {description}
            </Typography>
        </Box>
    )
}

// 카드형 선택 UI에서도 기존 입력 변경 핸들러가 받던 name/value 이벤트 형태를 유지한다.
const createFieldChangeEvent = (name, value) => ({
    target: {
        name,
        value,
    },
})

function RoleChoiceGroup({ value, options, disabled, onChange }) {
    return (
        <FormControl fullWidth>
            <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                등급
            </Typography>
            <Box
                role="radiogroup"
                aria-label="등급"
                sx={{
                    display: 'grid',
                    gridTemplateColumns: { xs: '1fr', md: `repeat(${options.length}, minmax(0, 1fr))` },
                    gap: 1,
                    mt: 1,
                }}
            >
                {options.map((option) => {
                    const selected = Number(value ?? 0) === option.value

                    return (
                        <FormControlLabel
                            key={option.value}
                            value={option.value}
                            control={(
                                <Radio
                                    name="role"
                                    checked={selected}
                                    onChange={() => onChange(createFieldChangeEvent('role', String(option.value)))}
                                    disabled={disabled}
                                />
                            )}
                            disabled={disabled}
                            sx={getChoiceCardSx(selected, disabled)}
                            label={(
                                <ChoiceCardLabel
                                    title={option.title}
                                    description={option.description}
                                />
                            )}
                        />
                    )
                })}
            </Box>
        </FormControl>
    )
}

function AccessModeChoiceGroup({ value, disabled, onChange }) {
    return (
        <FormControl fullWidth>
            <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                접근 범위
            </Typography>
            <Box
                role="radiogroup"
                aria-label="접근 범위"
                sx={{
                    display: 'grid',
                    gridTemplateColumns: { xs: '1fr', md: `repeat(${ACCESS_MODE_OPTIONS.length}, minmax(0, 1fr))` },
                    gap: 1,
                    mt: 1,
                }}
            >
                {ACCESS_MODE_OPTIONS.map((option) => {
                    const selected = (value || ADMIN_ACCESS_MODES.DEFAULT) === option.value

                    return (
                        <FormControlLabel
                            key={option.value}
                            value={option.value}
                            control={(
                                <Radio
                                    name="accessMode"
                                    checked={selected}
                                    onChange={() => onChange(createFieldChangeEvent('accessMode', option.value))}
                                    disabled={disabled}
                                />
                            )}
                            disabled={disabled}
                            sx={getChoiceCardSx(selected, disabled)}
                            label={(
                                <ChoiceCardLabel
                                    title={option.title}
                                    description={option.description}
                                />
                            )}
                        />
                    )
                })}
            </Box>
        </FormControl>
    )
}

export default function Admin(props) {
    const { role } = props
    const history = useHistory()
    const location = useLocation()
    const locationState = getLocationState(location)
    const id = props.match.params.adminId || locationState.id
    const curPage = locationState.curPage || 1
    const searchText = locationState.searchText || ''
    const searchSelect = locationState.searchSelect || DEFAULT_SEARCH_SELECT
    const listSearch = location.search || ''
    const draftKey = createEditAdminDraftKey(id)
    const [loading, setLoading] = useState(true)
    const [saving, setSaving] = useState(false)
    const [hasDetail, setHasDetail] = useState(false)
    const [draftReady, setDraftReady] = useState(false)
    const [data, setData] = useState({})
    const [detailErrorMessage, setDetailErrorMessage] = useState('')
    const [openSnackbar, setOpenSnackbar] = useState(false)
    const [severitySnackbar, setSeveritySnackbar] = useState('info')
    const [messageSnackbar, setMessageSnackbar] = useState('')
    const initialDraftSnapshotRef = useRef('')

    useEffect(() => {
        const getData = async () => {
            if (!id) {
                const errorMessage = '필수 파라미터가 없어 관리자 상세를 불러올 수 없습니다.'
                setDetailErrorMessage(errorMessage)
                setOpenSnackbar(true)
                setSeveritySnackbar('error')
                setMessageSnackbar(errorMessage)
                setLoading(false)
                return
            }

            setLoading(true)
            setHasDetail(false)
            setDraftReady(false)
            setDetailErrorMessage('')

            try {
                const response = await API.post('/api/v1/admin/admin/getAdmin', { adminId: id })
                if (response.data.code === 1) {
                    const baseData = response.data.data || {}
                    const storedDraft = readAdminDraft(draftKey)
                    const restoredDraft = applyAdminDraft({
                        baseData,
                        draft: storedDraft,
                    })

                    initialDraftSnapshotRef.current = JSON.stringify(createAdminDraftPayload({
                        data: baseData,
                    }))

                    setData(restoredDraft.data)
                    setHasDetail(Boolean(baseData?.adminId))
                    setDetailErrorMessage(baseData?.adminId ? '' : '관리자 상세 정보를 찾을 수 없습니다.')
                    setDraftReady(true)
                } else if (response.data.code === 403) {
                    history.push('/login')
                    return
                } else {
                    const errorMessage = response.data.message || '관리자 상세를 불러오지 못했습니다.'
                    setDetailErrorMessage(errorMessage)
                    setOpenSnackbar(true)
                    setSeveritySnackbar('error')
                    setMessageSnackbar(errorMessage)
                }
            } catch (_error) {
                const errorMessage = '관리자 상세를 불러오지 못했습니다.'
                setDetailErrorMessage(errorMessage)
                setOpenSnackbar(true)
                setSeveritySnackbar('error')
                setMessageSnackbar(errorMessage)
            } finally {
                setLoading(false)
            }
        }

        getData()
    }, [draftKey, history, id])

    useEffect(() => {
        if (!draftReady || !hasDetail) {
            return
        }

        // 관리자 상세는 목록 query를 건드리지 않고 편집 draft만 sessionStorage에 보존한다.
        const nextDraft = createAdminDraftPayload({
            data,
        })
        const nextDraftSnapshot = JSON.stringify(nextDraft)

        if (nextDraftSnapshot === initialDraftSnapshotRef.current) {
            clearAdminDraft(draftKey)
            return
        }

        saveAdminDraft(draftKey, nextDraft)
    }, [data, draftKey, draftReady, hasDetail])

    const handleClose = () => {
        setOpenSnackbar(false)
    }

    // 상세 조회 실패 시 목록 흐름과 동일한 빈 상태 안내를 사용한다.
    const renderDetailErrorState = () => (
        <ListEmptyState
            variant="error"
            title="관리자 상세를 불러오지 못했습니다."
            description={detailErrorMessage || '목록으로 돌아가 다시 확인하세요.'}
            action={(
                <BottomButtonGroup variant="emptyStateAction" path="/admins" search={listSearch} visible={false} searchText={searchText} searchSelect={searchSelect} curPage={curPage} onBackClick={() => clearAdminDraft(draftKey)} backLabel="목록으로"/>
            )}
            sx={{ mt: 2 }}
        />
    )

    const moveToList = (snackbarState) => {
        // 저장 후에도 운영자가 보던 관리자 목록 조건으로 그대로 복귀시킨다.
        clearAdminDraft(draftKey)
        history.push(createListLinkTarget({
            pathname: '/admins',
            search: listSearch,
            curPage: curPage,
            searchText: searchText,
            searchSelect: searchSelect,
            ...(snackbarState ? {
                openSnackbar: true,
                severitySnackbar: snackbarState.severity,
                messageSnackbar: snackbarState.message,
            } : {})
        }))
    }

    const handleUpdateField = (e) => {
        setData({
            ...data,
            [e.target.name]: e.target.value
        })
    }

    const updateData = async (params) => {
        try {
            setSaving(true)

            const response = await API({
                method: 'post',
                url: '/api/v1/admin/admin/update',
                data: params,
            })

            if (response.data.code === 1) {
                moveToList({ severity: 'success', message: '관리자 정보를 저장했습니다.' })
            } else if (response.data.code === 403) {
                history.push('/login')
            } else {
                setOpenSnackbar(true)
                setSeveritySnackbar('error')
                setMessageSnackbar(response.data.message)
            }
        } catch (_error) {
            setOpenSnackbar(true)
            setSeveritySnackbar('error')
            setMessageSnackbar('관리자 정보를 저장하지 못했습니다.')
        } finally {
            setSaving(false)
        }
    }

    const handleSubmit = (e) => {
        e.preventDefault()

        if (saving) {
            return
        }

        const nextEmail = (data.email || '').trim()
        const nextPhone = (data.phone || '').trim()
        const nextName = (data.name || '').trim()
        const nextMemo = (data.memo || '').trim()

        setData((prev) => ({
            ...prev,
            email: nextEmail,
            phone: nextPhone,
            name: nextName,
            memo: nextMemo
        }))

        if (!nextEmail || !nextPhone || !nextName) {
            setOpenSnackbar(true)
            setSeveritySnackbar('warning')
            setMessageSnackbar('이메일, 연락처, 이름/업체명을 입력하세요.')
            return
        }

        if (!EMAIL_REGEX.test(nextEmail)) {
            setOpenSnackbar(true)
            setSeveritySnackbar('warning')
            setMessageSnackbar('올바른 이메일을 입력하세요.')
            return
        }

        if (!PHONE_REGEX.test(nextPhone)) {
            setOpenSnackbar(true)
            setSeveritySnackbar('warning')
            setMessageSnackbar('올바른 연락처를 입력하세요.')
            return
        }

        updateData({
            ...data,
            email: nextEmail,
            phone: nextPhone,
            name: nextName,
            accessMode: data.accessMode || ADMIN_ACCESS_MODES.DEFAULT,
            memo: nextMemo
        })
    }

    if (loading) {
        return (
            <Grid item xs={12}>
                <Spiner/>
                {openSnackbar && <Snackbars open={openSnackbar} severity={severitySnackbar} message={messageSnackbar} handleClose={handleClose}/>}
            </Grid>
        )
    }

    if (!hasDetail) {
        return (
            <Grid item xs={12}>
                <Paper sx={{ p: 2, display: 'flex', flexDirection: 'column' }}>
                    <Title>어드민 수정</Title>
                    {renderDetailErrorState()}
                    {openSnackbar && <Snackbars open={openSnackbar} severity={severitySnackbar} message={messageSnackbar} handleClose={handleClose}/>}
                </Paper>
            </Grid>
        )
    }

    return (
        <Grid item xs={12}>
            <Paper sx={{ p: 2, display: 'flex', flexDirection: 'column' }}>
                <Title>어드민 수정</Title>
                <Box component="form" onSubmit={handleSubmit} sx={{ opacity: saving ? 0.6 : 1 }}>
                    <Grid container spacing={2} sx={{ marginTop: 2 }}>
                        <Grid item xs={12}>
                            <FormSection
                                title="권한 설정"
                                description="관리자가 사용할 수 있는 운영 권한 등급과 접근 범위를 선택합니다."
                            >
                                <RoleChoiceGroup
                                    value={data.role}
                                    options={getRoleOptions(role)}
                                    disabled={saving}
                                    onChange={handleUpdateField}
                                />
                                <Box sx={{ mt: 2 }}>
                                    <AccessModeChoiceGroup
                                        value={data.accessMode || ADMIN_ACCESS_MODES.DEFAULT}
                                        disabled={saving}
                                        onChange={handleUpdateField}
                                    />
                                </Box>
                            </FormSection>
                        </Grid>
                        <Grid item xs={12} sm={12} md={12}>
                            <FormControl fullWidth>
                                <TextField
                                    required
                                    label="아이디(이메일)"
                                    type="email"
                                    name="email"
                                    value={data.email || ''}
                                    onChange={handleUpdateField}
                                    size="small"
                                    disabled={saving}
                                />
                            </FormControl>
                        </Grid>
                        <Grid item xs={6} md={6} sm={12}>
                            <FormControl fullWidth>
                                <TextField
                                    required
                                    label="연락처"
                                    name="phone"
                                    value={data.phone || ''}
                                    onChange={handleUpdateField}
                                    size="small"
                                    disabled={saving}
                                />
                            </FormControl>
                        </Grid>
                        <Grid item xs={6} md={6} sm={12}>
                            <FormControl fullWidth>
                                <TextField
                                    required
                                    label="이름/업체명"
                                    name="name"
                                    value={data.name || ''}
                                    onChange={handleUpdateField}
                                    size="small"
                                    disabled={saving}
                                />
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} sm={12} md={12}>
                            <FormControl fullWidth>
                                <TextField
                                    label="메모"
                                    multiline
                                    rows={7}
                                    name="memo"
                                    value={data.memo || ''}
                                    onChange={handleUpdateField}
                                    size="small"
                                    disabled={saving}
                                />
                            </FormControl>
                        </Grid>
                    </Grid>
                    <BottomButtonGroup
                        visible={true}
                        submitDisabled={saving}
                        submitLabel={saving ? '저장 중...' : '저장'}
                        backDisabled={saving}
                        onBackClick={() => moveToList()}
                    />
                </Box>
                {openSnackbar && <Snackbars open={openSnackbar} severity={severitySnackbar} message={messageSnackbar} handleClose={handleClose}/>}
            </Paper>
        </Grid>
    )
}
